/**
 * Reusable currency input field component for forms with monetary values.
 *
 * This component provides a standardized way to handle currency inputs across
 * the application, with built-in currency display and form integration.
 *
 * Features:
 * - Integrated with React Hook Form for validation and state management
 * - Currency symbol/code display on the right side of input
 * - Support for decimal values (accepts "." and ",", always stores ".")
 * - Read-only mode support for view-only contexts
 * - Automatic number conversion and empty value handling
 * - Responsive design with proper spacing for currency display
 * - Rate masking support: when the form value is a masked string (e.g. "••••"),
 *   the field is disabled and renders the masked placeholder instead.
 *
 * Composition:
 * Delegates the sanitize-on-change loop to the shared `DecimalInput` primitive
 * from `decimal-form-field.tsx`. This file owns the currency-specific concerns:
 * masking, storing a NUMBER in RHF (not a string), local display state so
 * intermediate values like "12." survive keystrokes, label, suffix overlay,
 * dynamic padding, and loading shimmer.
 *
 * Usage:
 * ```tsx
 * <InputFieldWithCurrency
 *   name="projectBudget"
 *   label="Project Budget"
 *   placeholder="Enter budget amount"
 *   control={form.control}
 *   currency="USD"
 *   isReadMode={false}
 * />
 * ```
 */
import { CSSProperties, useEffect, useState } from 'react';

import { Control, ControllerRenderProps } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useTextWidth } from '@/modules/common/hooks/use-text-width';
import { RateMask } from '@/modules/common/utils/rate-mask';
import { isNumber } from '@/utils/number/is-number';

import {
  DecimalInput,
  DEFAULT_MAX_DECIMAL_PLACES,
  normalizeDecimalInput,
} from './decimal-form-field';

type CurrencyInputValueType = 'number' | 'string';

/**
 * Props interface for the InputFieldWithCurrency component.
 * Extends standard form field props with currency-specific options.
 */
interface InputFieldWithCurrencyProps {
  /** Form field name for React Hook Form integration */
  name: string;
  /** Display label for the input field */
  label?: string;
  /** Placeholder text shown when field is empty */
  placeholder?: string;
  /** React Hook Form control object for form state management */
  control: Control<any>;
  /** Currency code or symbol to display (e.g., "USD", "EUR", "$") */
  currency?: string;
  /** Custom suffix text to display instead of currency (e.g., "USD / mon", "USD / hr", "h") */
  suffixText?: string;
  /** Whether the field should be read-only */
  isReadMode: boolean;
  /** Type used to store the normalized value in the form. */
  valueType?: CurrencyInputValueType;
  /** Whether to render React Hook Form's field-level validation message. */
  showFormMessage?: boolean;
  /** Whether the field is in a loading state (disables input and shows shimmer animation) */
  isLoading?: boolean;
  /** Maximum number of decimal places allowed (default: {@link DEFAULT_MAX_DECIMAL_PLACES}). */
  maxDecimalPlaces?: number;
  /**
   * Max digits BEFORE the separator. Extra keystrokes are dropped so the input
   * stops accepting digits past the limit.
   */
  maxIntegerDigits?: number;
  /** Optional numeric floor. Enforced on blur only. */
  minValue?: number;
  /**
   * Maximum allowed value. When provided, takes precedence over `maxIntegerDigits`.
   * The digit cap is derived from the value itself (e.g. 366 → 3 integer digits).
   * Enforced on blur — values above the cap are clamped to `maxValue`.
   */
  maxValue?: number;
  /** When true, a leading minus sign is preserved (for discount/credit values). */
  allowNegative?: boolean;
  /** When true, clearing the input preserves an empty value instead of resetting to zero. */
  allowEmpty?: boolean;
  /**
   * Accepted for backwards compatibility with the previous native-number
   * implementation. No longer enforced at runtime — validation should live in
   * the form's Zod schema.
   */
  step?: number;
  /**
   * Accepted for backwards compatibility with the previous native-number
   * implementation. No longer enforced at runtime — validation should live in
   * the form's Zod schema.
   */
  max?: number;
}

/**
 * Inner input with LOCAL display state.
 *
 * React Hook Form stores the field as a NUMBER (for downstream calculations),
 * but the user needs to see intermediate values like "12." or "12,5" while
 * typing — otherwise pressing "." after a number appears to do nothing because
 * Number("12.") === 12 and the input rerenders as "12".
 *
 * We keep a `displayValue` string locally, sync it from `field.value` when the
 * form value changes externally, and forward a numeric value to RHF on change.
 * Sanitization itself is delegated to `DecimalInput`.
 */
interface RateInputProps {
  field: ControllerRenderProps<Record<string, unknown>, string>;
  placeholder?: string;
  disabled: boolean;
  isLoading: boolean;
  maxDecimalPlaces: number;
  maxIntegerDigits?: number;
  maxValue?: number;
  minValue?: number;
  allowNegative: boolean;
  allowEmpty: boolean;
  valueType: CurrencyInputValueType;
  paddingStyle: CSSProperties;
  isMasked: boolean;
}

const RateInput = ({
  field,
  placeholder,
  disabled,
  isLoading,
  maxDecimalPlaces,
  maxIntegerDigits,
  maxValue,
  minValue,
  allowNegative,
  allowEmpty,
  valueType,
  paddingStyle,
  isMasked,
}: RateInputProps) => {
  const resolvedMaxIntegerDigits =
    maxValue !== undefined ? String(Math.floor(maxValue)).length : maxIntegerDigits;
  // Initial display value: the masked string if masked, otherwise the numeric
  // value coerced to string (empty for 0/undefined to match previous UX).
  const computeExternalDisplay = (value: unknown): string => {
    if (isMasked) return RateMask.applyToString(value as string | undefined);
    if (value === undefined || value === null || value === '') return '';
    return String(value);
  };

  const [displayValue, setDisplayValue] = useState<string>(() =>
    computeExternalDisplay(field.value)
  );

  // Sync local display with external form value changes (e.g. when selecting
  // a different "invoice by" source populates the rate programmatically).
  // We only overwrite if the external numeric value differs from our current
  // parsed display, so we don't clobber user-in-progress typing like "12.".
  useEffect(() => {
    const externalDisplay = computeExternalDisplay(field.value);
    const parsedLocal = Number(displayValue.replace(/,/g, '.'));
    const parsedExternal = Number(externalDisplay);
    const bothAreNumbers = !isNaN(parsedLocal) && !isNaN(parsedExternal);
    if (!bothAreNumbers || parsedLocal !== parsedExternal) {
      setDisplayValue(externalDisplay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value, isMasked]);

  const handleBlur = () => {
    if (maxValue !== undefined) {
      const numValue = Number(displayValue);
      if (!isNaN(numValue) && numValue > maxValue) {
        const clamped = String(maxValue);
        setDisplayValue(clamped);
        field.onChange(valueType === 'number' ? maxValue : clamped);
      }
    }
    field.onBlur();
  };

  return (
    <DecimalInput
      name={field.name}
      onBlur={handleBlur}
      // When masked, suppress the "decimal" inputMode so the masked placeholder
      // isn't treated as a numeric field by mobile keyboards.
      inputMode={isMasked ? undefined : 'decimal'}
      placeholder={placeholder}
      disabled={disabled}
      maxDecimalPlaces={maxDecimalPlaces}
      maxIntegerDigits={resolvedMaxIntegerDigits}
      minValue={minValue}
      allowNegative={allowNegative}
      allowEmpty={allowEmpty}
      value={displayValue}
      onValueChange={normalized => {
        // Keep the normalized string locally so "12." survives between keystrokes.
        setDisplayValue(normalized);
        // A bare "-" is an incomplete negative number — keep it in display
        // but don't forward to RHF (avoids useEffect overwriting it to "0").
        if (normalized === '-') return;
        if (valueType === 'string') {
          field.onChange(normalized);
          return;
        }

        // Forward a NUMBER to RHF. A trailing dot parses as the integer part
        // so existing calculations don't blow up mid-typing.
        const numValue = Number(normalized);
        field.onChange(isNaN(numValue) ? 0 : numValue);
      }}
      className={`w-full${isLoading ? ' animate-pulse' : ''}`}
      style={paddingStyle}
    />
  );
};

/**
 * Currency input field component with integrated currency display.
 *
 * Renders a decimal input with a currency indicator positioned on the right
 * side. Handles automatic conversion between string display and numeric form
 * values, with proper handling of empty states, decimal precision, and rate
 * masking.
 *
 * @param props - Component props including form integration and display options
 * @returns Fully integrated form field with currency input and display
 */
export function InputFieldWithCurrency({
  name,
  label,
  placeholder,
  control,
  currency,
  suffixText,
  isReadMode,
  isLoading = false,
  maxDecimalPlaces = DEFAULT_MAX_DECIMAL_PLACES,
  maxIntegerDigits,
  maxValue,
  minValue,
  allowNegative = false,
  allowEmpty = false,
  valueType = 'number',
  showFormMessage = true,
}: InputFieldWithCurrencyProps) {
  // Get the text to display (prioritize suffixText over currency)
  const displayText = suffixText || currency || '';

  // Calculate dynamic padding based on text width
  const { paddingStyle } = useTextWidth(displayText);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const isMasked =
          !allowNegative && isNumber(field.value) && RateMask.isMaskedStringValue(field.value);

        return (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <div className="relative">
                <RateInput
                  field={field as ControllerRenderProps<Record<string, unknown>, string>}
                  placeholder={placeholder}
                  disabled={isReadMode || isMasked || isLoading}
                  isLoading={isLoading}
                  maxDecimalPlaces={maxDecimalPlaces}
                  maxIntegerDigits={maxIntegerDigits}
                  maxValue={maxValue}
                  minValue={minValue}
                  allowNegative={allowNegative}
                  allowEmpty={allowEmpty}
                  valueType={valueType}
                  paddingStyle={paddingStyle}
                  isMasked={isMasked}
                />
                {/* Currency indicator positioned on the right side */}
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transform text-sm text-muted-foreground">
                  {displayText}
                </span>
              </div>
            </FormControl>
            {showFormMessage && <FormMessage />}
          </FormItem>
        );
      }}
    />
  );
}

// Re-export for backwards compatibility with any consumer that may still import
// the utility from this file. The canonical source lives in decimal-form-field.tsx.
export { normalizeDecimalInput };
